import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { DecimalPipe } from '@angular/common';
import { AfterViewInit, Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import {
  ChartDataInterface,
  ChartDataType,
  ChartLegendPosition,
  ChartTheme,
} from '@app/shared/components/charts/interfaces/chart.interface';
import { CapitalizePipe } from '@app/shared/pipes/capitalize.pipe';
import { cloneDeep } from 'lodash';
import { CurrencyService } from '@app/shared/services/currency/currency.service';
import { ChartHelper } from '@app/shared/components/charts/helpers/chart.helper';

@Component({
  selector: 'generic-line-chart',
  templateUrl: './generic-line-chart.component.html',
  styleUrls: ['./generic-line-chart.component.scss'],
})
export class GenericLineChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() chartId = 'lineChart';
  @Input() title: string;
  @Input() data: ChartDataInterface[] = [];
  @Input() series: string[];
  @Input() verticalGrid = false;
  @Input() horizontalGrid = false;
  @Input() chartHeight = '600px';
  @Input() bullets = false;
  @Input() dataType: ChartDataType = ChartDataType.NUMBER;
  @Input() capitalizeSeriesLabels = true;
  @Input() extendedLabels = false;
  @Input() theme: ChartTheme = ChartTheme.WHITE;
  @Input() yAxisTitle: string;
  @Input() xAxisTitle: string;
  @Input() rotateLabels = false;
  @Input() xAxisLabelsDisabled = false;
  // string template for tooltip
  // e.g. `{name}: [bold]{valueY}[/]`
  @Input() tooltipTemplatePattern: string;
  @Input() legendPosition: ChartLegendPosition = 'top';
  @Input() maxValueYAxisNumber: number;
  @Input() additionalLabelsEnabled = false;

  private chart: am4charts.XYChart;
  private readonly fixedColors = [
    am4core.color('#43CFA1'),
    am4core.color('#FD6295'),
    am4core.color('#FFC107'),
    am4core.color('#f02f22'),
    am4core.color('#00fffb'),
    am4core.color('#ffc226'),
    am4core.color('#643cbb'),
    am4core.color('#a33408'),
    am4core.color('#ffec17'),
    am4core.color('#5f9bd9'),
    am4core.color('#483334'),
    am4core.color('#8f8559'),
    am4core.color('#1a8777'),
    am4core.color('#dae0f0'),
  ];
  private availableDataTypes = ChartDataType;
  private chartData: ChartDataInterface[] = [];
  private negativeValuesEnabled = false;

  constructor(
    private readonly zone: NgZone,
    private readonly capitalizePipe: CapitalizePipe,
    private readonly currencyService: CurrencyService,
    private readonly decimalPipe: DecimalPipe,
    private readonly chartHelper: ChartHelper,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dataChange = changes['data'];
    if (dataChange && typeof dataChange.previousValue !== 'undefined') {
      this.loadChart();
    }
  }

  ngAfterViewInit(): void {
    this.loadChart();
  }

  ngOnDestroy(): void {
    this.disposeChart();
  }

  private prepareChart(): void {
    this.chartData = cloneDeep(this.data);
    this.checkIfChartHasNegativeValues();
    this.checkPercentage();
    this.checkCapitalize();
    am4core.useTheme(am4themes_animated);
    const chart = am4core.create(this.chartId, am4charts.XYChart);
    chart.paddingBottom = 150;
    chart.colors.list = this.fixedColors;
    chart.data = this.chartHelper.prepareChartData(this.chartData, this.series);

    const title = chart.titles.create();
    title.text = this.title;
    title.fontSize = 20;
    title.marginBottom = 10;
    title.marginTop = 10;
    title.align = 'center';

    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = 'seriesName';
    if (this.additionalLabelsEnabled) {
      categoryAxis.renderer.labels.template.adapter.add('textOutput', (text, label) => {
        if (text) {
          return text.replace(/-\d+$/, '');
        }
      });
    }
    categoryAxis.renderer.grid.template.disabled = !this.verticalGrid;
    categoryAxis.renderer.grid.template.fill =
      this.chartHelper.getThemeColor(this.theme);
    categoryAxis.renderer.grid.template.stroke =
      this.chartHelper.getThemeColor(this.theme);
    categoryAxis.renderer.minGridDistance = 50;
    categoryAxis.renderer.labels.template.fill = this.chartHelper.getThemeColor(this.theme);
    categoryAxis.cursorTooltipEnabled = false;
    categoryAxis.startLocation = 0.5;
    categoryAxis.endLocation = 0.5;
    if (this.rotateLabels) {
      categoryAxis.renderer.labels.template.rotation = 270;
      categoryAxis.renderer.labels.template.horizontalCenter = 'left';
      categoryAxis.renderer.labels.template.verticalCenter = 'middle';
    }

    if (this.xAxisTitle) {
      categoryAxis.title.text = this.xAxisTitle;
      categoryAxis.title.rotation = 0;
      categoryAxis.title.align = 'center';
      categoryAxis.title.valign = 'middle';
      categoryAxis.title.fill =
        this.chartHelper.getThemeColor(this.theme);
    }
    if (this.xAxisLabelsDisabled) {
      categoryAxis.renderer.labels.template.disabled = true;
    }
    const yAxis = chart.yAxes.push(new am4charts.ValueAxis());
    yAxis.renderer.grid.template.fill =
      this.chartHelper.getThemeColor(this.theme);
    yAxis.renderer.grid.template.stroke =
      this.chartHelper.getThemeColor(this.theme);
    yAxis.renderer.grid.template.disabled = !this.horizontalGrid;
    if (!this.negativeValuesEnabled) {
      yAxis.min = 0;
    }
    yAxis.renderer.labels.template.fill =
      this.chartHelper.getThemeColor(this.theme);
    yAxis.cursorTooltipEnabled = false;
    yAxis.renderer.baseGrid.stroke =
      this.chartHelper.getThemeColor(this.theme);
    if (this.yAxisTitle) {
      yAxis.title.text = this.yAxisTitle;
      yAxis.title.rotation = 270;
      yAxis.title.align = 'center';
      yAxis.title.valign = 'middle';
      yAxis.title.fill =
        this.chartHelper.getThemeColor(this.theme);
    }
    if (this.dataType === this.availableDataTypes.PERCENTAGE) {
      yAxis.renderer.labels.template.adapter.add('text', (text, label) => {
        return label.dataItem['value'] + '%';
      });
    }
    if (this.dataType === this.availableDataTypes.CURRENCY) {
      yAxis.renderer.labels.template.adapter.add('text', (text, label) => {
        return this.currencyService.currencySymbol + this.decimalPipe.transform(label.dataItem['value'], '1.');
      });
    }
    if (this.dataType === this.availableDataTypes.NUMBER) {
      yAxis.renderer.labels.template.adapter.add('text', (text, label) => {
        return this.decimalPipe.transform(label.dataItem['value'], '1.');
      });
    }

    if (!this.negativeValuesEnabled) {
      yAxis.min = 0;
    }

    if (this.maxValueYAxisNumber) {
      yAxis.max = this.maxValueYAxisNumber;
    }

    const createSeries = (name: string): am4charts.LineSeries => {
      const series = chart.series.push(new am4charts.LineSeries());
      series.name = name;
      series.dataFields.categoryX = 'seriesName';
      series.dataFields.valueY = name;
      series.tooltipText = this.getTooltipText();
      series.tensionX = 0.7;
      series.tensionY = 1;
      series.yAxis = yAxis;
      series.strokeWidth = 3;
      const interfaceColors = new am4core.InterfaceColorSet();
      if (this.bullets) {
        const bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.stroke = interfaceColors.getFor('background');
        bullet.circle.strokeWidth = 1;
      }

      return series;
    };

    this.series.forEach(element => {
      createSeries(element);
    });

    chart.legend = new am4charts.Legend();
    chart.legend.position = this.legendPosition;
    chart.legend.labels.template.fill =
      this.chartHelper.getThemeColor(this.theme);

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineY.disabled = true;
    chart.cursor.lineX.disabled = true;

    this.chart = chart;
  }

  private prepareChartWithExtendedLabels(): void {
    this.chartData = cloneDeep(this.data);
    this.checkIfChartHasNegativeValues();
    this.checkPercentage();
    this.checkCapitalize();
    am4core.useTheme(am4themes_animated);
    const chart = am4core.create(this.chartId, am4charts.XYChart);
    chart.paddingBottom = 50;
    chart.colors.list = this.fixedColors;
    chart.data = this.chartHelper.prepareChartDataWithExtendedLabels(this.chartData, this.series);

    const title = chart.titles.create();
    title.text = this.title;
    title.fontSize = 20;
    title.marginBottom = 10;
    title.marginTop = 10;
    title.align = 'center';

    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = 'seriesName';
    if (this.additionalLabelsEnabled) {
      categoryAxis.renderer.labels.template.adapter.add('textOutput', (text, label) => {
        if (text) {
          return text.replace(/-\d+$/, '');
        }
      });
    }
    categoryAxis.renderer.grid.template.disabled = !this.verticalGrid;
    categoryAxis.renderer.grid.template.fill =
      this.chartHelper.getThemeColor(this.theme);
    categoryAxis.renderer.grid.template.stroke =
      this.chartHelper.getThemeColor(this.theme);
    categoryAxis.renderer.minGridDistance = 20;
    categoryAxis.renderer.labels.template.fill =
      this.chartHelper.getThemeColor(this.theme);
    categoryAxis.cursorTooltipEnabled = false;
    categoryAxis.startLocation = 0.5;
    categoryAxis.endLocation = 0.5;
    if (this.xAxisTitle) {
      categoryAxis.title.text = this.xAxisTitle;
      categoryAxis.title.rotation = 0;
      categoryAxis.title.align = 'center';
      categoryAxis.title.valign = 'middle';
      categoryAxis.title.fill =
        this.chartHelper.getThemeColor(this.theme);
    }

    if (this.rotateLabels) {
      categoryAxis.renderer.labels.template.rotation = 270;
      categoryAxis.renderer.labels.template.horizontalCenter = 'left';
      categoryAxis.renderer.labels.template.verticalCenter = 'middle';
    }
    if (this.xAxisLabelsDisabled) {
      categoryAxis.renderer.labels.template.disabled = true;
    }
    const yAxis = chart.yAxes.push(new am4charts.ValueAxis());
    yAxis.renderer.grid.template.fill =
      this.chartHelper.getThemeColor(this.theme);
    yAxis.renderer.grid.template.stroke =
      this.chartHelper.getThemeColor(this.theme);
    yAxis.renderer.grid.template.disabled = !this.horizontalGrid;
    yAxis.renderer.baseGrid.stroke =
      this.chartHelper.getThemeColor(this.theme);
    if (!this.negativeValuesEnabled) {
      yAxis.min = 0;
    }
    if (this.yAxisTitle) {
      yAxis.title.text = this.yAxisTitle;
      yAxis.title.rotation = 270;
      yAxis.title.align = 'center';
      yAxis.title.valign = 'middle';
      yAxis.title.fill =
        this.chartHelper.getThemeColor(this.theme);
    }
    yAxis.renderer.labels.template.fill =
      this.chartHelper.getThemeColor(this.theme);
    yAxis.cursorTooltipEnabled = false;
    if (this.dataType === this.availableDataTypes.PERCENTAGE) {
      yAxis.renderer.labels.template.adapter.add('text', (text, label) => {
        return label.dataItem['value'] + '%';
      });
    }
    if (this.dataType === this.availableDataTypes.CURRENCY) {
      yAxis.renderer.labels.template.adapter.add('text', (text, label) => {
        return this.currencyService.currencySymbol + label.dataItem['value'];
      });
    }

    const createSeries = (name: string, index: number): am4charts.LineSeries => {
      const series = chart.series.push(new am4charts.LineSeries());
      series.name = name;
      series.dataFields.categoryX = 'seriesName';
      series.dataFields.valueY = name;
      series.tooltipText = this.getTooltipTextWithExtendedLabels(index);
      series.tensionX = 0.7;
      series.tensionY = 1;
      series.yAxis = yAxis;
      series.strokeWidth = 3;
      const interfaceColors = new am4core.InterfaceColorSet();
      if (this.bullets) {
        const bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.stroke = interfaceColors.getFor('background');
        bullet.circle.strokeWidth = 1;
      }

      return series;
    };

    this.series.forEach((element, index) => {
      createSeries(element, index);
    });

    chart.legend = new am4charts.Legend();
    chart.legend.position = this.legendPosition;
    chart.legend.labels.template.fill =
      this.chartHelper.getThemeColor(this.theme);

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineY.disabled = true;
    chart.cursor.lineX.disabled = true;

    this.chart = chart;
  }

  private checkPercentage(): void {
    if (this.dataType === this.availableDataTypes.PERCENTAGE) {
      this.chartData.forEach((item, index) => {
        const values = item.values.map(value => Number((value * 100).toFixed(2)));
        this.chartData[index].values = values;
      });
    }
  }

  private checkCapitalize(): void {
    if (this.capitalizeSeriesLabels) {
      this.series = this.series.map(item => this.capitalizePipe.transform(item));
    }
  }

  private checkIfChartHasNegativeValues(): void {
    this.chartData.forEach(element => {
      if (element.values.some(item => item < 0)) {
        this.negativeValuesEnabled = true;
      }
    });
  }

  private getTooltipText(): string {
    if (typeof this.tooltipTemplatePattern !== 'undefined') {
      return this.tooltipTemplatePattern;
    }
    if (this.dataType === ChartDataType.CURRENCY) {
      return this.xAxisLabelsDisabled
        ? `{categoryX}\n{name}: [bold]${this.currencyService.currencySymbol}{valueY}[/]`
        : `{name}: [bold]${this.currencyService.currencySymbol}{valueY.formatNumber("#.#")}[/]`;
    }
    if (this.dataType === ChartDataType.PERCENTAGE) {
      return this.xAxisLabelsDisabled
        ? '{categoryX}\n{name}: [bold]{valueY.formatNumber("#.#")}%[/]'
        : `{name}: [bold]{valueY.formatNumber("#.#")}%[/]`;
    }
    return this.xAxisLabelsDisabled
      ? '{categoryX}\n{name}: [bold]{valueY.formatNumber("#.")}[/]'
      : `{name}: [bold]{valueY.formatNumber("#.")}[/]`;
  }

  private getTooltipTextWithExtendedLabels(index: number): string {
    if (this.dataType === ChartDataType.CURRENCY) {
      return `{label${index}}\n{name}: [bold]${this.currencyService.currencySymbol}{valueY.formatNumber("#.")}[/]`;
    }
    if (this.dataType === ChartDataType.PERCENTAGE) {
      return `{label${index}}\n{name}: [bold]{valueY.formatNumber("#.#")}%[/]`;
    }
    return `{label${index}}\n{name}: [bold]{valueY.formatNumber("#.")}[/]`;
  }

  private checkIsSingleDataElement(): void {
    // if there is only one data element we cannot draw chart line - it will be single point
    // line can be draw between two points so we need to copy that element
    // and make small hack with `item` property since chart cannot have two same `item` values
    if (this.data.length === 1) {
      const dataElement = { ...this.data[0] };
      this.data.push(dataElement);
      this.data[1].item = `${dataElement.item} `;
    }
  }

  private loadChart(): void {
    am4core.unuseAllThemes();
    if (this.chart) {
      this.disposeChart();
    }
    this.zone.runOutsideAngular(() => {
      this.checkIsSingleDataElement();
      if (!this.extendedLabels) {
        this.prepareChart();
      } else {
        this.prepareChartWithExtendedLabels();
      }
    });
  }

  private disposeChart(): void {
    this.zone.runOutsideAngular(() => {
      if (this.chart) {
        this.chart.dispose();
      }
    });
  }
}
